import { useState, useEffect } from 'react';
import { InputValidations, useTranslation, Theme, FAIcon } from '@nextgen-web-framework/all';
import * as Yup from 'yup';
import { useField } from 'formik';
import { PasswordWithConfirmProps } from '../../types/propTypes';
import AmwayPasswordField from '../../amway-password-field/AmwayPasswordField';
import styles from '../../AmwayPasswordInput.module.scss';
import { InputFieldProps, PasswordVisible } from '../../amway-password-field/types/propTypes';
import { CreatePasswordFormSchema } from '../../../create-password-form/CreatePasswordForm.schema';
import PasswordStrengthMeter from '../../../password-strength/PasswordStrengthMeter';

const PasswordWithConfirm = (props: PasswordWithConfirmProps) => {
  const t = useTranslation();
  const {
    placeholderForCreatePassword = t('identity.CreatePasswordForm.createPasswordPlaceholderLabel'),
    placeholderForCreatePasswordError = t('identity.CreatePasswordForm.createPasswordPlaceholderLabelError'),
    placeholderForConfirmPassword = t('identity.CreatePasswordForm.confirmPasswordPlaceholderLabel'),
    createPasswordError = t('identity.CreatePasswordForm.createPasswordError'),
    confirmPasswordError = t('identity.CreatePasswordForm.confirmPasswordError'),
    passwordCombinationError1 = t('identity.CreatePasswordForm.passwordCombinationError1'),
    passwordCombinationError2 = t('identity.CreatePasswordForm.passwordCombinationError2'),
    passwordCombinationError3 = t('identity.CreatePasswordForm.passwordCombinationError3'),
    passwordCombinationErrorHeading = t('identity.CreatePasswordForm.passwordCombinationErrorHeading'),
    setIsButtonDisable,
    value,
    name,
    onInputChangeCallback,
    className,
  } = props;
  // eslint-disable-next-line
  const [field, meta, helpers] = useField(name);
  const [passwordVisible, setPasswordVisible] = useState<PasswordVisible>({
    createPassword: false,
    confirmPassword: false,
  });
  const [touched, setTouched] = useState({
    createPassword: false,
    confirmPassword: false,
  });
  const [values, setValues] = useState({
    createPassword: value?.createPassword || '',
    confirmPassword: value?.confirmPassword || '',
  });

  const [errors, setErrors] = useState({
    createPassword: '',
    confirmPassword: '',
  });

  const [focused, setFocused] = useState({
    createPassword: false,
    confirmPassword: false,
  });
  const togglePasswordVisibility = (name: string) => {
    setPasswordVisible({
      ...passwordVisible,
      [name]: !passwordVisible[name as keyof typeof passwordVisible],
    });
  };

  const { NEXT_PUBLIC_IDENTITY_ALLOW_PASSWORD_STRENGTH_METER: allowPasswordStrengthMeter } = ConfigService.getConfigs();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { name, value } = e.target;
    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    helpers.setValue(value);
    onInputChangeCallback?.(e);
  };

  useEffect(() => {
    const resetErrorsAndDisableButton = () => {
      setErrors({
        createPassword: '',
        confirmPassword: '',
      });
      setIsButtonDisable?.(true);
    };

    const setValidationErrors = (
      error: Yup.ValidationError,
      validationErrors: {
        confirmPassword: string;
        createPassword: string;
      },
    ) => {
      if (touched[error.path as keyof typeof touched]) {
        validationErrors[error.path as keyof typeof validationErrors] = error.message;
      }
    };
    const updateConditionalErrors = (validationErrors: { confirmPassword: string; createPassword: string }) => {
      if (values.confirmPassword && !values.createPassword) {
        validationErrors.confirmPassword = validationErrors.confirmPassword || '';
        validationErrors.createPassword = '';
      } else if (!values.confirmPassword) {
        validationErrors.confirmPassword = '';
      }
    };
    const handleValidationErrors = (err: unknown) => {
      const validationErrors = {
        createPassword: '',
        confirmPassword: '',
      };

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          setValidationErrors(error, validationErrors);
          updateConditionalErrors(validationErrors);
        });
      }

      setErrors(validationErrors);
      setIsButtonDisable?.(true);
    };

    const validateForm = async () => {
      if (!values.createPassword && !values.confirmPassword) {
        resetErrorsAndDisableButton();
        return;
      }

      try {
        await CreatePasswordFormSchema.validate(values, { abortEarly: false });
        setErrors({
          createPassword: '',
          confirmPassword: '',
        });
        setIsButtonDisable?.(false);
      } catch (err) {
        handleValidationErrors(err);
      }
    };

    validateForm();
  }, [values, touched, setIsButtonDisable]);

  const getInputField = ({ values, name, placeholder, onChange, isError, disabled }: InputFieldProps) => (
    <AmwayPasswordField
      values={values}
      data-testid={`${name}-input-test-id`}
      type={passwordVisible[name] ? 'text' : 'password'}
      name={name}
      placeholder={placeholder}
      label={placeholder}
      onChange={onChange}
      onKeyDown={onChange}
      minLength={8}
      inputIcon={passwordVisible[name] ? { iconName: 'eye' } : { iconName: 'eye-slash' }}
      inputIconOnClick={() => togglePasswordVisibility(name)}
      status={isError ? InputValidations.INVALID : InputValidations.DEFAULT}
      onFocus={() => setFocused({ ...focused, [name]: true })}
      onBlur={() => setFocused({ ...focused, [name]: false })}
      disabled={disabled}
    />
  );
  const isError = Object.values(errors).some((e: string) => e.length > 0);
  const isErrorIcon =
    (touched.createPassword && errors.createPassword.length > 0) ||
    (touched.confirmPassword && errors.confirmPassword.length > 0);

  const createpasswordClass = `${styles['create-password-input']} ${
    className == 'updatepassword'
      ? styles['create-password-input-updatepassword']
      : styles['create-password-input-forgotpassword']
  }`;
  const confirmPasswordClass = `${styles['confirm-password-input']} ${
    className == 'updatepassword'
      ? styles['confirm-password-input-updatepassword']
      : styles['confirm-password-input-forgotpassword']
  }`;
  return (
    // Create Password field
    <div className={styles['password-input-fields']} data-testid="password-with-confirm-testid">
      <div className={createpasswordClass}>
        {getInputField({
          values: values.createPassword,
          name: 'createPassword',
          placeholder: isError ? placeholderForCreatePasswordError : placeholderForCreatePassword,
          onChange: handleChange,
          isError: isError,
        })}
      </div>
      {/* Password Strength Meter */}
      {allowPasswordStrengthMeter === true && (
        <div className={styles['password-strength-meter']}>
          <PasswordStrengthMeter password={values.createPassword} isFocused={focused.createPassword} />
        </div>
      )}{' '}
      {
        <div className={confirmPasswordClass}>
          {/* confirm password section */}
          {getInputField({
            values: values.confirmPassword,
            name: 'confirmPassword',
            placeholder: placeholderForConfirmPassword,
            onChange: handleChange,
            isError: isError,
          })}
        </div>
      }
      {/* Validation section, not need when password strength meter is on */}
      {allowPasswordStrengthMeter !== true && isError && (
        <div className={styles['sections']}>
          <div className={styles['left-section']}>
            {/* error icon */}
            <div className="icon-section">
              {isErrorIcon && <FAIcon theme={Theme.ERROR} iconName={'square-exclamation'} />}
            </div>

            {/* password policy error message list */}
            <div data-testid="error-msg-test-id" className={styles['error-section']}>
              {touched.createPassword && errors.createPassword.length > 0 && (
                <div className={styles['confirmpassword-error-form']}>
                  {passwordCombinationErrorHeading}
                  <ul className={styles['password-rules']}>
                    <li className={styles['bullet-point-rules']}>{passwordCombinationError1}</li>
                    <li className={styles['bullet-point-rules']}>{passwordCombinationError2}</li>
                    <li className={styles['bullet-point-rules']}>{passwordCombinationError3}</li>
                  </ul>
                </div>
              )}
              {touched.createPassword && errors.createPassword.length > 0 && (
                <div className={styles['createpassword-error-form']}>
                  <ul className={styles['password-rules']}>
                    <div className={styles['bullet-point-rules']}>
                      <li>{createPasswordError}</li>
                    </div>
                  </ul>
                </div>
              )}
              {touched.confirmPassword && errors.confirmPassword.length > 0 && (
                <div className={styles['confirmpassword-error-form']}>{confirmPasswordError}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordWithConfirm;
